import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { CSSProperties } from 'react';
import { useT } from '../locales';
import { DATE_FORMAT } from '../lib/date';

const { RangePicker } = DatePicker;

/**
 * Par de fechas como strings YYYY-MM-DD. Cada extremo puede faltar
 * (rango parcial o filtro vacío). `null` completo significa sin filtro.
 */
export type DateRangeValue = [string | null, string | null];

export interface DateRangeFilterIds {
  start?: string;
  end?: string;
}

export interface DateRangeFilterProps {
  /** Par de fechas (YYYY-MM-DD) o null cuando no hay filtro. */
  value?: DateRangeValue | null;
  /** Recibe el rango seleccionado como strings YYYY-MM-DD, o null al limpiar. */
  onChange?: (value: DateRangeValue | null) => void;
  /** Placeholders de cada extremo (por defecto "Rango de fechas"). */
  placeholder?: [string, string];
  /** IDs forwarded to the start and end inputs for accessible associations. */
  id?: DateRangeFilterIds;
  style?: CSSProperties;
}

/**
 * Wrapper de DatePicker.RangePicker con API de strings YYYY-MM-DD y
 * periodos de negocio predefinidos (presets). La conversión a Dayjs es
 * interna; los consumidores nunca ven dayjs.
 */
export function DateRangeFilter({ value, onChange, placeholder, id, style }: DateRangeFilterProps) {
  const t = useT();

  const dayjsValue: [Dayjs | null, Dayjs | null] | null = value
    ? [value[0] ? dayjs(value[0]) : null, value[1] ? dayjs(value[1]) : null]
    : null;

  const presets: { label: string; value: [Dayjs, Dayjs] }[] = [
    {
      label: t('common.periodoUltimoMes'),
      value: [
        dayjs().subtract(1, 'month').startOf('month'),
        dayjs().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      label: t('common.periodoUltimos3Meses'),
      value: [dayjs().subtract(3, 'month'), dayjs()],
    },
    {
      label: t('common.periodoUltimos6Meses'),
      value: [dayjs().subtract(6, 'month'), dayjs()],
    },
    {
      label: t('common.periodoAnioActual'),
      value: [dayjs().startOf('year'), dayjs()],
    },
    {
      label: t('common.periodoAnioAnterior'),
      value: [
        dayjs().subtract(1, 'year').startOf('year'),
        dayjs().subtract(1, 'year').endOf('year'),
      ],
    },
  ];

  return (
    <RangePicker
      value={dayjsValue}
      id={id}
      onChange={(dates) => {
        onChange?.(
          dates
            ? [dates[0]?.format('YYYY-MM-DD') ?? null, dates[1]?.format('YYYY-MM-DD') ?? null]
            : null,
        );
      }}
      format={DATE_FORMAT}
      placeholder={placeholder ?? [t('common.rangoFechas'), t('common.rangoFechas')]}
      presets={presets}
      allowClear
      style={{ width: 300, ...style }}
    />
  );
}
